import { useMemo, useState } from 'react';
import { Menu as MuiMenu, MenuItem, useTheme } from '@mui/material';
import { styled } from '@mui/system';

import SearchBar from '../SearchBar/SearchBar';
import { ListOption } from '../SelectDropdown';
import { MenuBody } from '../SelectDropdown/MenuBody';
import Tooltip from '../Tooltip';
import { MenuProps } from './types';

const Menu = ({
  anchorEl,
  disabledTooltipMessage,
  handleClose,
  isMenuSearchable = false,
  maxHeight,
  onChange,
  open,
  options,
  value,
  width = 233,
}: MenuProps) => {
  const [searchText, setSearchText] = useState('');
  const theme = useTheme();

  const selectedOption = useMemo(
    () => options.find(option => option.value === value),
    [options, value],
  );

  const filteredOptions = useMemo(() => {
    if (isMenuSearchable) {
      return options.filter(option =>
        option.label.toLowerCase().includes(searchText.trim().toLowerCase()),
      );
    }
    return options;
  }, [isMenuSearchable, options, searchText]);

  const renderMenuItem = (menuProps: ListOption) => {
    const isSelectedOption =
      typeof menuProps.value === 'string' &&
      menuProps.value === selectedOption?.value;

    const menuItemBody = (
      <MenuItem
        disabled={menuProps.disabled}
        key={menuProps.value}
        onClick={() => {
          onChange(menuProps.value);
          handleClose();
        }}
        sx={[
          {
            '& .MuiButton-root': {
              display: 'none',
            },
            '&.Mui-focusVisible': {
              backgroundColor: 'rgba(0,0,0,0.05)',
            },
            '&.Mui-selected': {
              backgroundColor: 'transparent',
            },
            '&:hover': {
              '& .MuiButton-root': {
                display: 'flex',
              },
              backgroundColor: `${theme.palette.colors.purple[100]} !important`,
            },
            alignItems: 'center',
            backgroundColor: 'transparent',
            display: 'flex',
            justifyContent: 'space-between',
            paddingLeft: 2 + (menuProps.indent || 0) * 2,
          },
        ]}
        value={menuProps.value}
      >
        <MenuBody
          inputValue={value}
          isSelectedOption={isSelectedOption}
          {...menuProps}
        />
      </MenuItem>
    );

    return menuProps.disabled && disabledTooltipMessage ? (
      <DisabledOptionContainer>
        <Tooltip tooltipContent={disabledTooltipMessage}>
          {menuItemBody}
        </Tooltip>
      </DisabledOptionContainer>
    ) : (
      menuItemBody
    );
  };

  return (
    <MuiMenu
      anchorEl={anchorEl}
      keepMounted
      onClose={handleClose}
      open={open}
      PaperProps={{
        elevation: 2,
        sx: {
          border: `1px solid ${theme.palette.colors.grey[500]}`,
          maxHeight,
          width,
        },
      }}
      sx={{
        marginTop: '8px',
      }}
    >
      {isMenuSearchable && (
        <MenuItem
          onClickCapture={e => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onKeyDown={e => {
            // allow keyboard navigation away from this item
            if (!['ArrowDown', 'ArrowUp'].includes(e.key)) {
              e.stopPropagation();
            }
          }}
          sx={{
            backgroundColor: `${theme.palette.colors.white} !important`,
            padding: '8px 16px',
          }}
        >
          <SearchBar
            size='small'
            placeholder='Search'
            value={searchText}
            onChange={({ target }) => setSearchText(target.value)}
          />
        </MenuItem>
      )}
      {filteredOptions.map(renderMenuItem)}
    </MuiMenu>
  );
};

const DisabledOptionContainer = styled('div')`
  & > div {
    display: block;
    width: 100%;
  }
`;

export default Menu;
